import { useCallback, useEffect, useMemo, useRef } from 'react';

import { debounce } from '@mui/material/utils';

import { useApp, SAVE_STATUSES } from '../contextProviders/AppProvider';

interface UseAutoSaveOptions<T> {
  updateFn: ((changes: Partial<T>) => Promise<void>) | undefined;
  itemId: string;
  additionalFields?: Partial<T>;
  debounceMs?: number;
  enabled?: boolean;
}

interface UseAutoSaveReturn<T> {
  queueChange: <K extends keyof T>(field: K, value: T[K]) => void;
  flushChanges: () => Promise<void>;
  cancelChanges: () => void;
}

/**
 * A reusable hook for auto-saving changes with debounce.
 * Automatically updates the global saveStatus in AppProvider.
 *
 * @param options.updateFn - The function to call to save changes
 * @param options.itemId - The ID of the item being edited (used for the update call)
 * @param options.debounceMs - Debounce delay in milliseconds (default: 1500)
 * @param options.enabled - Whether auto-save is enabled (default: true)
 */
export function useAutoSave<T extends { itemId: string }>({
  updateFn,
  itemId,
  additionalFields,
  debounceMs = 1500,
  enabled = true,
}: UseAutoSaveOptions<T>): UseAutoSaveReturn<T> {
  const { setSaveStatus } = useApp();
  const pendingChangesRef = useRef<Partial<T>>({});
  const updateFnRef = useRef(updateFn);
  const itemIdRef = useRef(itemId);
  const additionalFieldsRef = useRef(additionalFields);

  // Keep refs updated
  updateFnRef.current = updateFn;
  itemIdRef.current = itemId;
  additionalFieldsRef.current = additionalFields;

  // The actual save function
  const performSave = useCallback(async () => {
    const changes = pendingChangesRef.current;
    if (Object.keys(changes).length === 0 || !updateFnRef.current) return;

    setSaveStatus(SAVE_STATUSES.SAVING);
    try {
      await updateFnRef.current({ itemId: itemIdRef.current, ...additionalFieldsRef.current, ...changes } as Partial<T>);
      pendingChangesRef.current = {};
      setSaveStatus(SAVE_STATUSES.SAVED);
    } catch (error) {
      console.error('Auto-save failed:', error);
      setSaveStatus(SAVE_STATUSES.ERROR);
    }
  }, [setSaveStatus]);

  // Debounced save function
  const debouncedSave = useMemo(
    () => debounce(performSave, debounceMs),
    [performSave, debounceMs]
  );

  // Queue a change to be saved
  const queueChange = useCallback(
    <K extends keyof T>(field: K, value: T[K]) => {
      if (!enabled || !updateFnRef.current) return;

      pendingChangesRef.current[field] = value;
      debouncedSave();
    },
    [enabled, debouncedSave]
  );

  // Flush any pending changes immediately
  const flushChanges = useCallback(async () => {
    debouncedSave.clear();
    await performSave();
  }, [debouncedSave, performSave]);

  // Discard all pending changes without saving (e.g., before delete/archive)
  const cancelChanges = useCallback(() => {
    debouncedSave.clear();
    pendingChangesRef.current = {};
  }, [debouncedSave]);

  // Save on unmount
  useEffect(() => {
    if (!enabled) return;

    return () => {
      debouncedSave.clear();
      const changes = pendingChangesRef.current;
      if (Object.keys(changes).length > 0 && updateFnRef.current) {
        // Fire and forget on unmount - we can't await here
        updateFnRef.current({ itemId: itemIdRef.current, ...changes } as Partial<T>);
      }
    };
  }, [enabled, debouncedSave]);

  return { queueChange, flushChanges, cancelChanges };
}
